import React, { useState, useEffect, SyntheticEvent } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";

import Header from "./Header";
import RecordingCard from "../../components/cards/RecordingCard";
import FlowCard from "../../components/cards/FlowCard";
import { RootState } from "../../store/rootReducer";
import { Recording, Scrambling, Workflow } from "../../declarations/interface";
import {
  actions,
  getProjectById,
  updateWorkflowName,
  updateRecordingName,
  updateProjectName,
  deleteWorkflow,
  deleteRecording,
} from "../../slices/project";
import { actions as projectsActions } from "../../slices/projects";
import { generateFlowUrl } from "../../util/UtilService";
import useSnackbar from "../../hooks/useSnackbar";
import ScramblingContainer from "./ScramblingContainer";

type ProjectTab = "RECORDINGS" | "FLOWS" | "SCRAMBLING";

const useStyles = makeStyles((theme) => ({
  body: {
    maxHeight: "calc(100vh - 116px)",
    overflow: "auto",
    height: "100%",
  },
  contentCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  fullWidth: {
    maxWidth: "100%",
  },
}));

const ProjectDetail = () => {
  const classes = useStyles();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<ProjectTab>("RECORDINGS");
  const [selectedScrambling, setSelectedScrambling] = useState<
    Scrambling | undefined
  >();

  const { notify, hideNotification } = useSnackbar();

  const { project, status } = useSelector((state: RootState) => state.project);
  const dispatch = useDispatch();

  const handleNameUpdate = (name: string) => {
    dispatch(updateProjectName({ id: projectId, name }));
  };
  const handleTabChange = (_: SyntheticEvent<Element, Event>, tab: string) => {
    const params = { tab: "1" };
    if (tab === "FLOWS") params.tab = "2";
    if (tab === "SCRAMBLING") params.tab = "3";
    setSearchParams(params);
  };
  const getCount = () => {
    if (activeTab === "FLOWS") return project?.workflow_lists?.length || 0;
    if (activeTab === "RECORDINGS")
      return project?.recordings_lists?.length || 0;
    if (activeTab === "SCRAMBLING") return project?.scramblings?.length || 0;
    return 0;
  };
  const handleSnackbarClose = () => {
    hideNotification();
    dispatch(actions.clearStatus());
  };

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "1") {
      setActiveTab("RECORDINGS");
    } else if (tab === "2") {
      setActiveTab("FLOWS");
    } else if (tab === "3") {
      setActiveTab("SCRAMBLING");
    }
  }, [searchParams, setSearchParams, setActiveTab]);

  useEffect(() => {
    dispatch(getProjectById(projectId));
  }, [projectId]);

  useEffect(() => {
    if (status) {
      notify({
        message: status.message,
        onClose: handleSnackbarClose,
        type: status.type,
      });
    }
  }, [status]);

  useEffect(() => {
    if (project) dispatch(projectsActions.loadRecordingsAndFlows(project));
  }, [project]);

  const handleEditScrambling = (scrambling: Scrambling) => {
    setSelectedScrambling(scrambling);
  };

  return (
    <Box height="100%">
      {project && (
        <Header
          projectName={project.project_name}
          projectId={project.project_id}
          onProjectNameUpdate={handleNameUpdate}
          count={getCount()}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
          selectedScrambling={selectedScrambling}
          removeSelectedScrambling={() => {
            setSelectedScrambling(undefined);
          }}
        />
      )}
      {project && (
        <Box className={classes.body}>
          {activeTab === "RECORDINGS" && (
            <Recordings list={project.recordings_lists} />
          )}
          {activeTab === "FLOWS" && <Flows list={project.workflow_lists} />}
          {activeTab === "SCRAMBLING" && (
            <ScramblingContainer
              list={project.scramblings ?? []}
              onEditClick={handleEditScrambling}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const Recordings: React.FC<{
  list: Recording[];
}> = ({ list }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleNameUpdate = (id: string, name: string) => {
    dispatch(updateRecordingName({ name, id }));
  };
  const handleDelete = (id: string) => {
    dispatch(deleteRecording(id));
  };
  return (
    <>
      {list.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(4, minmax(0, 1fr))"
          columnGap={2}
          rowGap={2}
          px={4.5}
        >
          {list.map((recording: Recording, index: number) => (
            <RecordingCard
              key={index}
              id={recording.recording_id}
              name={recording.recording_name}
              createdOn={recording.createdAt}
              onClick={() => {
                navigate({
                  pathname: `/recording/${recording.recording_id}`,
                });
              }}
              onNameUpdate={handleNameUpdate}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      )}
      {list.length === 0 && (
        <Box className={classes.contentCenter}>
          <Typography variant="h5" color="primary">
            All recordings created for this project will be visible here
          </Typography>
        </Box>
      )}
    </>
  );
};

const Flows: React.FC<{
  list: Workflow[];
}> = ({ list }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleNameUpdate = (id: string, name: string) => {
    dispatch(updateWorkflowName({ name, id }));
  };
  const handleDelete = (id: string) => {
    dispatch(deleteWorkflow(id));
  };
  return (
    <>
      {list.length > 0 && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(2, minmax(0, 1fr))"
          columnGap={2}
          rowGap={2}
          px={4.5}
        >
          {list.map((flow: Workflow, index: number) => (
            <FlowCard
              key={index}
              id={flow.workflow_id}
              name={flow.workflow_name}
              createdOn={flow.createdAt}
              link={
                flow?.workflow_entry_url || generateFlowUrl(flow.workflow_id)
              }
              onClick={() => {
                navigate({
                  pathname: `/flow/${flow.workflow_id}`,
                });
              }}
              onNameUpdate={handleNameUpdate}
              onDelete={handleDelete}
            />
          ))}
        </Box>
      )}
      {list.length === 0 && (
        <Box className={classes.contentCenter}>
          <Typography variant="h5" color="primary">
            All flows created for this project will be visible here
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ProjectDetail;
